/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import appConfig from '../../../ui-config.json';

const API_PREFIX = `/${appConfig.appName}-ui-request-handler`;
const INVOKE_PREFIX = `${API_PREFIX}/invoke`;

export const BACKEND_BASE = '/api';

export function redirectToLogin(): void {
  window.location.assign(`/${appConfig.appName}/login`);
}

export interface ApiErrorBody {
  code?: string;
  message?: string;
  fields?: Record<string, string>;
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  fields?: Record<string, string>;

  constructor(status: number, message: string, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code ?? body?.type ?? 'unknown_error';
    this.fields = body?.fields;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

export interface InvokeOptions extends RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  base?: string;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;

  const headers: Record<string, string> = {};
  let requestBody: BodyInit | undefined;
  if (body instanceof URLSearchParams) {
    requestBody = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    requestBody = JSON.stringify(body);
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    method,
    headers,
    body: requestBody,
    credentials: 'include',
  });

  const parsed = await parseBody(response);

  if (!response.ok) {
    const message = typeof parsed === 'string' ? parsed : response.statusText;
    throw new ApiError(response.status, message);
  }

  return parsed as T;
}

function buildQuery(query: InvokeOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function isEnvelope(value: unknown): value is { success: boolean; data: unknown; error: unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in value &&
    'data' in value &&
    'error' in value
  );
}

function toApiError(status: number, parsed: unknown, fallback: string): ApiError {
  const raw = isEnvelope(parsed) ? parsed.error : (parsed as { error?: unknown })?.error ?? parsed;
  if (raw && typeof raw === 'object') {
    const body = raw as ApiErrorBody;
    const message = body.message ?? body.detail ?? body.title ?? fallback;
    return new ApiError(status, message, body);
  }
  return new ApiError(status, typeof parsed === 'string' && parsed ? parsed : fallback);
}

export async function invoke<T>(path: string, options: InvokeOptions = {}): Promise<T> {
  const { method = 'GET', body, query, base = BACKEND_BASE } = options;

  const headers: Record<string, string> = {};
  let requestBody: BodyInit | undefined;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    requestBody = JSON.stringify(body);
  }

  const response = await fetch(`${INVOKE_PREFIX}${base}${path}${buildQuery(query)}`, {
    method,
    headers,
    body: requestBody,
    credentials: 'include',
  });

  const parsed = await parseBody(response);

  if (response.status === 401) {
    redirectToLogin();
    throw toApiError(401, parsed, 'Not authenticated');
  }

  if (!response.ok) {
    throw toApiError(response.status, parsed, response.statusText || 'Request failed');
  }

  if (response.status === 204 || parsed === undefined) {
    return undefined as T;
  }

  if (isEnvelope(parsed)) {
    if (!parsed.success) {
      throw toApiError(response.status, parsed, 'Request failed');
    }
    return parsed.data as T;
  }

  return parsed as T;
}
